<template>
    <toolbar-option :message="message" type="stacked">
        <template slot="secondary">
            <dropdown
                :options="spacingOptions"
                :value="spacingValue"
                native
                @input="onSpacingInput"
            />
        </template>

        <template slot="primary">
            <directional-pad :value="alignmentValue" @input="onAlignmentInput" />
        </template>
    </toolbar-option>
</template>

<script>
import baseOption from '@/options/mixins/base-option';
import { getClassDefinition } from '@/utils/tailwind-helper';
import mapFilters from '@/utils/map-filters';
import DirectionalPad from '@/components/utilities/directional-pad';

const inputToValueMap = {
    column: {
        alignItems: {
            topLeft: 'start',
            top: 'center',
            topRight: 'end',
            left: 'start',
            middle: 'center',
            right: 'end',
            bottomLeft: 'start',
            bottom: 'center',
            bottomRight: 'end',
        },

        justifyContent: {
            topLeft: 'start',
            top: 'start',
            topRight: 'start',
            left: 'center',
            middle: 'center',
            right: 'center',
            bottomLeft: 'end',
            bottom: 'end',
            bottomRight: 'end',
        },
    },

    'column-reverse': {
        alignItems: {
            topLeft: 'start',
            top: 'center',
            topRight: 'end',
            left: 'start',
            middle: 'center',
            right: 'end',
            bottomLeft: 'start',
            bottom: 'center',
            bottomRight: 'end',
        },

        justifyContent: {
            topLeft: 'end',
            top: 'end',
            topRight: 'end',
            left: 'center',
            middle: 'center',
            right: 'center',
            bottomLeft: 'start',
            bottom: 'start',
            bottomRight: 'start',
        },
    },

    row: {
        alignItems: {
            topLeft: 'start',
            top: 'start',
            topRight: 'start',
            left: 'center',
            middle: 'center',
            right: 'center',
            bottomLeft: 'end',
            bottom: 'end',
            bottomRight: 'end',
        },

        justifyContent: {
            topLeft: 'start',
            top: 'center',
            topRight: 'end',
            left: 'start',
            middle: 'center',
            right: 'end',
            bottomLeft: 'start',
            bottom: 'center',
            bottomRight: 'end',
        },
    },

    'row-reverse': {
        alignItems: {
            topLeft: 'start',
            top: 'start',
            topRight: 'start',
            left: 'center',
            middle: 'center',
            right: 'center',
            bottomLeft: 'end',
            bottom: 'end',
            bottomRight: 'end',
        },

        justifyContent: {
            topLeft: 'end',
            top: 'center',
            topRight: 'start',
            left: 'end',
            middle: 'center',
            right: 'start',
            bottomLeft: 'end',
            bottom: 'center',
            bottomRight: 'start',
        },
    },
};

// prop syntax = <alignItemsValue>_<justifyContentValue>
const valueToInputMap = {
    column: {
        start_start: 'topLeft',
        center_start: 'top',
        end_start: 'topRight',
        start_center: 'left',
        center_center: 'middle',
        end_center: 'right',
        start_end: 'bottomLeft',
        center_end: 'bottom',
        end_end: 'bottomRight',
    },

    'column-reverse': {
        start_start: 'bottomLeft',
        center_start: 'bottom',
        end_start: 'bottomRight',
        start_center: 'left',
        center_center: 'middle',
        end_center: 'right',
        start_end: 'topLeft',
        center_end: 'top',
        end_end: 'topRight',
    },

    row: {
        start_start: 'topLeft',
        start_center: 'top',
        start_end: 'topRight',
        center_start: 'left',
        center_center: 'middle',
        center_end: 'right',
        end_start: 'bottomLeft',
        end_center: 'bottom',
        end_end: 'bottomRight',
    },

    'row-reverse': {
        start_start: 'topRight',
        start_center: 'top',
        start_end: 'topLeft',
        center_start: 'right',
        center_center: 'middle',
        center_end: 'left',
        end_start: 'bottomRight',
        end_center: 'bottom',
        end_end: 'bottomLeft',
    },
};

const spacingValues = ['between', 'around'];

export default {
    name: 'content-alignment',
    components: { DirectionalPad },
    mixins: [baseOption],

    data() {
        return {
            displayName: 'Content Alignment',
            tailwindDefinitionProp: '',
            alignmentValue: '',
            spacingValue: '',
        };
    },

    computed: {
        message() {
            return '* this option will not have any effect if there’s not enough surrounding space';
        },

        spacingOptions() {
            const options = spacingValues.map((value) => {
                return {
                    text: `Space ${ this.titleCase(value) }`,
                    value,
                };
            });

            options.unshift({ text: 'No Spacing', value: '' });

            return options;
        },
    },

    watch: {
        changedOption({ option }) {
            if (option === 'flex-direction') {
                this.setFlexDirection();

                if (this.value) {
                    this.onInput(this.value);
                }
            }
        },
    },

    methods: {
        ...mapFilters(['titleCase']),

        onAlignmentInput(valueNew) {
            // reset the input if a directional button is pressed twice
            if (valueNew === this.alignmentValue) {
                this.alignmentValue = '';
                this.removeFlexClass(null, this.screen, 'alignItems');
                return;
            }

            const valueAlign = inputToValueMap[this.flexDirection].alignItems[valueNew];
            const flexClassAlign = this.addFlexClass({ tailwindDefinitionProp: 'alignItems', value: valueAlign });

            this.alignmentValue = valueNew;
            this.onOptionChange(flexClassAlign);

            // when there's a spacing value set (e.g. space-between or space-around)
            // ... no other justify-content value has any effect
            if (!this.spacingValue) {
                const valueJustify = inputToValueMap[this.flexDirection].justifyContent[valueNew];
                const flexClassJustify = this.addFlexClass({ tailwindDefinitionProp: 'justifyContent', value: valueJustify });

                this.onOptionChange(flexClassJustify);
            }
        },

        onSpacingInput(valueNew) {
            this.spacingValue = valueNew;

            const flexClass = this.addFlexClass({
                tailwindDefinitionProp: 'justifyContent',
                value: valueNew,
            });

            this.onOptionChange(flexClass);

            if (!valueNew) {
                // if the spacing is removed, we reset the alignment classes to their original state
                this.onAlignmentInput(this.alignmentValue);
            }
        },

        onSelectedElement() {
            this.setFlexDirection();

            // get the current align-items value
            this.tailwindDefinitionProp = 'alignItems';
            const valueAlign = this.flexClassDefinition.value;

            // get the current justify-content value
            this.tailwindDefinitionProp = 'justifyContent';
            const valueJustify = this.flexClassDefinition.value;

            if (spacingValues.includes(valueJustify)) {
                this.spacingValue = valueJustify;

                // if justify-content is set to space-between or space-around
                // ... then none of its related buttons on the directional pad will have any effect
                // ... so we just default to "center" by default
                this.alignmentValue = valueToInputMap[this.flexDirection][`${ valueAlign }_center`];
            } else {
                this.alignmentValue = valueToInputMap[this.flexDirection][`${ valueAlign }_${ valueJustify }`];
            }
        },

        setFlexDirection(className) {
            const newDirection = className ? getClassDefinition(className).value : '';

            this.flexDirection = newDirection || this.getComputedValue('flex-direction');
        },
    },
};
</script>
